import { theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';

// Dark counterpart of `antdTheme.ts`'s `zoomLogiAntdTheme`. Based on antd's
// own darkAlgorithm (so every component antd ships, including ones this
// codebase doesn't hand-tune below, still gets a coherent dark rendering),
// layered with overrides for the same components zoomLogiAntdTheme already
// covers, so the tested surface matches what light mode defines.
//
// Deliberately omits non-color tokens (borderRadius, controlHeight, etc.) --
// those aren't active in light mode today (antd's stock defaults are in
// effect there), so copying them here would smuggle a layout change into
// what should be a colors-only feature.
export const zoomLogiAntdDarkTheme: ThemeConfig = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    colorPrimary: '#4C8DFF',
    colorSuccess: '#34D399',
    colorSuccessBg: 'rgba(52, 211, 153, 0.14)',
    colorError: '#FF5C72',
    colorErrorBg: 'rgba(255, 92, 114, 0.14)',
    colorWarning: '#F0B429',
    colorWarningBg: 'rgba(240, 180, 41, 0.14)',
    colorInfo: '#5B9DFF',
    colorInfoBg: 'rgba(91, 157, 255, 0.14)',
    colorLink: '#4C8DFF',
    colorLinkHover: '#7BAEFF',
    colorLinkActive: '#3A78E0',
    colorText: '#F2F3F5',
    colorTextSecondary: '#AFAFAF',
    colorTextTertiary: '#7A7F87',
    colorBorder: '#33363D',
    colorBorderSecondary: '#24262B',
    colorBgContainer: '#17191E',
    colorBgElevated: '#1F2126',
    colorBgLayout: '#101317',
    controlItemBgHover: '#23262C',
    controlItemBgActive: 'rgba(76, 141, 255, 0.16)',
    // Same seed as the light-mode ConfigProvider in App.tsx: AiInsightsChatModal's
    // portal sits at z-index 1040, above the map/HUD (<=1000) -- keep popups above it.
    zIndexPopupBase: 1100,
  },
  components: {
    Alert: {
      colorSuccess: '#34D399',
      colorSuccessBg: 'rgba(52, 211, 153, 0.14)',
      colorError: '#FF5C72',
      colorErrorBg: 'rgba(255, 92, 114, 0.14)',
      colorWarning: '#F0B429',
      colorWarningBg: 'rgba(240, 180, 41, 0.14)',
      colorInfo: '#5B9DFF',
      colorInfoBg: 'rgba(91, 157, 255, 0.14)',
    },
    Badge: {
      colorSuccess: '#34D399',
      colorError: '#FF5C72',
      colorWarning: '#F0B429',
      colorInfo: '#5B9DFF',
    },
    Button: {
      colorPrimary: '#4C8DFF',
      colorPrimaryHover: '#7BAEFF',
      colorPrimaryActive: '#3A78E0',
      dangerColor: '#FFFFFF',
      colorError: '#FF5C72',
      colorErrorHover: '#FF7F91',
      colorErrorActive: '#E0384F',
    },
    Card: {
      colorBgContainer: '#17191E',
      colorBorderSecondary: '#24262B',
    },
    Dropdown: {
      colorBgElevated: '#1F2126',
      controlItemBgHover: '#23262C',
      controlItemBgActive: 'rgba(76, 141, 255, 0.16)',
      controlItemBgActiveHover: 'rgba(76, 141, 255, 0.20)',
    },
    Menu: {
      itemBg: '#17191E',
      itemHoverBg: '#23262C',
      itemSelectedBg: 'rgba(76, 141, 255, 0.16)',
      itemSelectedColor: '#7BAEFF',
    },
    Select: {
      colorBgContainer: '#17191E',
      colorBgElevated: '#1F2126',
      colorBorder: '#33363D',
      optionActiveBg: '#23262C',
      optionSelectedBg: 'rgba(76, 141, 255, 0.16)',
      optionSelectedColor: '#7BAEFF',
      multipleItemBg: '#23262C',
      multipleItemColorDisabled: '#5A5F68',
    },
    Message: {
      contentBg: '#2A2E36',
    },
    Tag: {
      defaultBg: '#23262B',
      defaultColor: '#AFAFAF',
    },
    Tabs: {
      itemActiveColor: '#F2F3F5',
      itemHoverColor: '#7BAEFF',
      itemSelectedColor: '#F2F3F5',
      inkBarColor: '#4C8DFF',
    },
  },
};
